import { first, isUndefined } from 'lodash';
import moment from 'moment';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

import { api } from '../../../api';
import { Enrolment } from '../../../api/types/enrolment';
import { Session } from '../../../api/types/session';
import { Student } from '../../../api/types/student';
import { ESSLRecord, SubscriptionDetails } from '../../../api/types/subscription';
import useBillingInformationQuery from '../../../hooks/queries/useBillingInformationQuery.js';
import { ENROLMENT_STATUS } from '../../../types/hubEnums';
import { BillingInformationType } from '../../../types/hubPropTypes';
import { formatError } from '../../../util/errors';
import { ESSLRecordHelper, ESSL_RECORDS_ACTIONS } from './ClueyPlus/ESSLRecordHelper';
import { SUBSCRIPTION_LOCAL_STORAGE_KEYS, SubscriptionLocalStorage } from './SubscriptionLocalStorage';

export enum SUBSCRIPTION_TYPE {
  BUNDLED = 'Bundled',
  TRIAL = 'Trial',
  CLUEY_PLUS = 'Cluey+',
}

export enum CONFIRMATION_TYPE {
  CLUEY_PLUS_DISCOUNT_APPLIED = 'CLUEY_PLUS_DISCOUNT_APPLIED',
  CLUEY_PLUS_SUBSCRIPTION_CREATED = 'CLUEY_PLUS_SUBSCRIPTION_CREATED',
  CLUEY_PLUS_DOWNGRADE_SUBSCRIPTION = 'CLUEY_PLUS_DOWNGRADE_SUBSCRIPTION',
  CLUEY_PLUS_SUBSCRIPTION_CANCELLED = 'CLUEY_PLUS_SUBSCRIPTION_CANCELLED',
}

export enum CANCELLATION_TYPE {
  CANCEL_TUTORIAL = 'CANCEL_TUTORIAL',
  CANCEL_CLUEY_PLUS = 'CANCEL_CLUEY_PLUS',
}

export type SubscriptionContextType = {
  clueyPlus: {
    isLoading: boolean;
    subscriptions: SubscriptionDetails[];
    esslRecords: ESSLRecord[];
    isESSLRecordsLoading: boolean;
    hasMultipleActiveSubscriptions: boolean;
    cancellation: {
      show: boolean;
      setShow: (show: boolean) => void;
      cancellationType: CANCELLATION_TYPE | null;
    };
    details: {
      show: boolean;
      setShow: (show: boolean) => void;
    };
    specialOffer: {
      show: boolean;
      setShow: (show: boolean) => void;
    };
    toast: {
      message: React.ReactNode;
      setMessage: (message: React.ReactNode) => void;
      show: boolean;
      setShow: (show: boolean) => void;
    };
    confirmation: {
      show: boolean;
      setShow: (show: boolean) => void;
      confirmationType: CONFIRMATION_TYPE | null;
    };
    canDowngradeSubscription: boolean;
    showStatusAsActive: boolean;
    refetchESSLRecords: () => void;
    getSubscription: (type?: SUBSCRIPTION_TYPE) => SubscriptionDetails | null;
    showDetailsDialog: () => void;
    showSpecialOfferDialog: () => void;
    showCancellationDialog: (cancellationType: CANCELLATION_TYPE) => void;
    showConfirmationDialog: (confirmationType: CONFIRMATION_TYPE) => void;
  };
  banner: {
    show: boolean;
    setShow: (show: boolean) => void;
    message: React.ReactNode;
    setMessage: (message: React.ReactNode) => void;
  };
  billing: {
    info: {
      billingInformation: BillingInformationType;
      isLoading: boolean;
      error: string | null;
    };
  };
  student: {
    students: Student[];
    studentError: string | null;
    studentIsLoading: boolean;
    selectedStudent: Student | null;
    setSelectedStudent: (student: Student | null) => void;
    selectedStudentEnrolments: Enrolment[];
  };
  enrolments: {
    active: {
      enrolments: Enrolment[];
      isLoading: boolean;
      error: string | null;
    };
  };
  sessions: {
    upcoming: {
      sessions: Session[];
      isLoading: boolean;
      error: string | null;
    };
    completed: {
      sessions: Session[];
      isLoading: boolean;
      error: string | null;
    };
  };
};

export const SubscriptionContext = createContext<SubscriptionContextType | null>(null);

export const SubscriptionContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [selectedStudentEnrolments, setSelectedStudentEnrolments] = useState<Enrolment[]>([]);
  const [bannerShow, setBannerShow] = useState(false);
  const [bannerMessage, setBannerMessage] = useState<React.ReactNode>(null);

  const {
    data: billingInformation,
    isLoading: isBillingInformationLoading,
    isFetching: isBillingInformationFetching,
    error: getBillingInformationError,
    refetch: refetchBillingInformation,
  } = useBillingInformationQuery();
  const isLoadingBillingInformation = isBillingInformationLoading || isBillingInformationFetching;
  const previousBillingStudentIdRef = useRef<string | null>(null);

  useEffect(() => {
    const studentId = selectedStudent?.studentId ?? null;
    if (!studentId) {
      previousBillingStudentIdRef.current = null;
      return;
    }
    const previousId = previousBillingStudentIdRef.current;
    previousBillingStudentIdRef.current = studentId;
    if (previousId !== null && previousId !== studentId) {
      void refetchBillingInformation();
    }
  }, [selectedStudent?.studentId, refetchBillingInformation]);

  // ClueyPlus related state
  const [isLoadingClueyPlus, setIsLoadingClueyPlus] = useState(true);
  const [subscriptions, setSubscriptions] = useState<SubscriptionDetails[]>([]);
  const [esslRecords, setESSLRecords] = useState<ESSLRecord[]>([]);
  const [hasMultipleActiveSubscriptions, setHasMultipleActiveSubscriptions] = useState(false);
  const [showSpecialOfferMessage, setShowSpecialOfferMessage] = useState(false);
  const [toastMessage, setToastMessage] = useState<React.ReactNode>(null);
  const [toastShow, setToastShow] = useState(false);
  const [detailsShow, setDetailsShow] = useState(true);
  const [canDowngradeSubscription, setCanDowngradeSubscription] = useState(false);
  const [showStatusAsActive, setShowStatusAsActive] = useState(false);
  const [cancellationShow, setCancellationShow] = useState(false);
  const [cancellationType, setCancellationType] = useState<CANCELLATION_TYPE | null>(null);
  const [confirmationShow, setConfirmationShow] = useState(false);
  const [confirmationType, setConfirmationType] = useState<CONFIRMATION_TYPE | null>(null);

  const { data: studentsData, isLoading: isLoadingStudents, error: studentError } = api.student.all.useQuery();
  const { data: subscriptionsData, isLoading: isLoadingSubscriptions } = api.subscription.getAll.useQuery();

  const {
    data: activeEnrolments,
    isLoading: isLoadingActiveEnrolments,
    error: activeEnrolmentsError,
  } = api.enrolment.all.useQuery('active');

  const { data: pastEnrolments, isLoading: isLoadingPastEnrolments } = api.enrolment.all.useQuery('past');

  const {
    data: upcomingSessions,
    isLoading: isLoadingUpcomingSessions,
    error: upcomingSessionsError,
  } = api.session.all.useQuery('upcoming');

  const {
    data: completedSessions,
    isLoading: isLoadingCompletedSessions,
    error: completedSessionsError,
  } = api.session.all.useQuery('completed');

  const {
    data: esslRecordsData,
    isLoading: isLoadingESSLRecords,
    refetch: refetchESSLRecords,
  } = api.subscription.esslRecords.useQuery(
    { studentId: selectedStudent?.studentId ?? '' },
    { enabled: !!selectedStudent?.studentId }
  );

  useEffect(() => {
    if (studentsData && !isLoadingStudents) {
      setStudents(
        studentsData.sort((a, b) => {
          const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
          const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
          return nameA.localeCompare(nameB);
        }) ?? []
      );
    }
  }, [studentsData, isLoadingStudents, students]);

  useEffect(() => {
    if (!isLoadingSubscriptions) {
      setIsLoadingClueyPlus(false);
    }
  }, [isLoadingSubscriptions]);

  useEffect(() => {
    if (esslRecordsData && !isLoadingESSLRecords && selectedStudent) {
      setESSLRecords(esslRecordsData.data ?? []);
    }
  }, [esslRecordsData, isLoadingESSLRecords, selectedStudent]);

  // Reset dialog/toast/banner state whenever the selected student changes
  useEffect(() => {
    if (!selectedStudent) {
      return;
    }
    setDetailsShow(true);
    setShowSpecialOfferMessage(false);
    setConfirmationType(null);
    setConfirmationShow(false);
    setToastMessage(null);
    setToastShow(false);
    setCancellationShow(false);
    setBannerShow(false);
    setBannerMessage(null);

    setShowStatusAsActive(false);
  }, [selectedStudent]);

  // Sync subscriptions and special-offer state from API + enrolments in one pass so we never read stale
  useEffect(() => {
    const dataReady = !isLoadingSubscriptions && subscriptionsData && !isLoadingActiveEnrolments && activeEnrolments;
    if (!selectedStudent || !dataReady) {
      return;
    }

    // Set the selected student's enrolments
    setSelectedStudentEnrolments(
      activeEnrolments.filter((enrolment) => enrolment.studentSfid === selectedStudent.studentId)
    );

    const studentSubscriptions = subscriptionsData.find(
      (subscription) => subscription.studentId === selectedStudent.studentId
    );
    const subs = !isUndefined(studentSubscriptions)
      ? studentSubscriptions.subscriptions.sort((a, b) => {
          return new Date(b.subscriptionPaidUntilDate).getTime() - new Date(a.subscriptionPaidUntilDate).getTime();
        })
      : [];
    setSubscriptions(subs);
    setHasMultipleActiveSubscriptions(
      subs.filter((subscription) => subscription.subscriptionStatus === 'Active').length > 1
    );

    const cleanUpLocalStorageIds = () => {
      // Check local storage for CLUEY_PLUS_CANCEL_SUBSCRIPTION_REQUESTED if there's subscription being cancelled
      // Check if the subscription is already Cancelled then remove the subscription from the list
      const subscriptionBeingCancelled = SubscriptionLocalStorage.get(
        SUBSCRIPTION_LOCAL_STORAGE_KEYS.CLUEY_PLUS_CANCEL_SUBSCRIPTION_REQUESTED
      );
      if (subscriptionBeingCancelled) {
        const subscriptionToRemove = subs.find(
          (subscription) =>
            subscriptionBeingCancelled.includes(subscription.subscriptionId) &&
            subscription.subscriptionStatus === 'Cancelled'
        );
        if (subscriptionToRemove) {
          SubscriptionLocalStorage.remove(
            SUBSCRIPTION_LOCAL_STORAGE_KEYS.CLUEY_PLUS_CANCEL_SUBSCRIPTION_REQUESTED,
            subscriptionToRemove?.subscriptionId || ''
          );
        }
      }

      // Check local storage for CLUEY_PLUS_SUBS_BEING_CREATED if there's subscription being created
      // Check if the subscription is already created and Active then remove the student id from the list
      const subscriptionBeingCreated = SubscriptionLocalStorage.get(
        SUBSCRIPTION_LOCAL_STORAGE_KEYS.CLUEY_PLUS_SUBS_BEING_CREATED
      );
      if (subscriptionBeingCreated) {
        const activeSubscription = first(subs);
        if (
          activeSubscription?.subscriptionType === SUBSCRIPTION_TYPE.CLUEY_PLUS &&
          activeSubscription?.subscriptionStatus === 'Active'
        ) {
          SubscriptionLocalStorage.remove(
            SUBSCRIPTION_LOCAL_STORAGE_KEYS.CLUEY_PLUS_SUBS_BEING_CREATED,
            selectedStudent.studentId
          );
          setDetailsShow(true);
          setConfirmationShow(false);
          setBannerShow(false);
          setBannerMessage(null);
        } else if (
          activeSubscription?.subscriptionType === SUBSCRIPTION_TYPE.TRIAL &&
          activeSubscription?.subscriptionPaidUntilDate &&
          moment(activeSubscription?.subscriptionPaidUntilDate).isBefore(moment(), 'day')
        ) {
          if (subscriptionBeingCreated?.includes(selectedStudent?.studentId)) {
            setConfirmationShow(true);
            setConfirmationType(CONFIRMATION_TYPE.CLUEY_PLUS_SUBSCRIPTION_CREATED);
          }
        }
      }

      // CLUEY_PLUS_DISCOUNTED_SUBS_BEING_CREATED
      const discountedSubscriptionBeingCreated = SubscriptionLocalStorage.get(
        SUBSCRIPTION_LOCAL_STORAGE_KEYS.CLUEY_PLUS_DISCOUNTED_SUBS_BEING_CREATED
      );
      if (discountedSubscriptionBeingCreated) {
        const activeSubscription = first(subs);
        if (
          activeSubscription?.subscriptionType === SUBSCRIPTION_TYPE.CLUEY_PLUS &&
          activeSubscription?.subscriptionStatus === 'Active' &&
          !!activeSubscription?.subscriptionDiscountPercentage &&
          activeSubscription?.subscriptionDiscountPercentage > 0
        ) {
          SubscriptionLocalStorage.remove(
            SUBSCRIPTION_LOCAL_STORAGE_KEYS.CLUEY_PLUS_DISCOUNTED_SUBS_BEING_CREATED,
            selectedStudent.studentId
          );
          setDetailsShow(true);
          setConfirmationShow(false);
          setBannerShow(false);
          setBannerMessage(null);
        }
      }
    };

    // CLUEY_PLUS_FREE_TRIAL_BEING_CREATED
    const freeTrialBeingCreated = SubscriptionLocalStorage.get(
      SUBSCRIPTION_LOCAL_STORAGE_KEYS.CLUEY_PLUS_FREE_TRIAL_BEING_CREATED
    );
    if (freeTrialBeingCreated) {
      const activeSubscription = first(subs);
      if (
        activeSubscription?.subscriptionType === SUBSCRIPTION_TYPE.TRIAL &&
        activeSubscription?.subscriptionStatus === 'Active'
      ) {
        SubscriptionLocalStorage.remove(
          SUBSCRIPTION_LOCAL_STORAGE_KEYS.CLUEY_PLUS_FREE_TRIAL_BEING_CREATED,
          selectedStudent.studentId
        );
        setDetailsShow(true);
        setConfirmationShow(false);
        setBannerShow(false);
        setBannerMessage(null);
      }
    }

    // Checks Bundled special cases:
    // - Showing of special offer
    // - Cancelled Bundle subscription, display them as Active and show Active until detail
    const bundledSubscriptionSpecialCases = () => {
      const activeSubscriptionIsBundled = first(subs)?.subscriptionType === SUBSCRIPTION_TYPE.BUNDLED;
      const cancelRequestedEnrolments = SubscriptionLocalStorage.get(
        SUBSCRIPTION_LOCAL_STORAGE_KEYS.CLUEY_PLUS_CANCEL_ENROLMENT_REQUESTED
      );

      // Reset special cases flags/states for Bundled subscription
      if (!activeSubscriptionIsBundled) {
        setShowSpecialOfferMessage(false);
        setCanDowngradeSubscription(false);
        return;
      }

      const studentEnrolments = activeEnrolments.filter(
        (enrolment) => enrolment.studentSfid === selectedStudent.studentId
      );
      const studentActiveEnrolments = studentEnrolments.filter(
        (enrolment) => ![ENROLMENT_STATUS.CANCEL_REQUESTED].includes(enrolment.enrolmentStatus)
      );
      const hasCancelledRequestedEnrolment = studentEnrolments.some(
        (enrolment) => enrolment.enrolmentStatus === ENROLMENT_STATUS.CANCEL_REQUESTED
      );
      const hasPendingCancelRequestedEnrolment = cancelRequestedEnrolments.some((enrolmentId) =>
        activeEnrolments.some((enrolment) => enrolment.enrolmentId === enrolmentId)
      );
      const subscriptionIsPassedPUD =
        first(subs)?.subscriptionStatus === 'Cancelled' &&
        moment(first(subs)?.subscriptionPaidUntilDate).isBefore(moment(), 'day');
      const enrolmentHasNoActiveEnrolment =
        studentActiveEnrolments.length === 0 && (hasCancelledRequestedEnrolment || hasPendingCancelRequestedEnrolment);
      setShowSpecialOfferMessage(subscriptionIsPassedPUD ? false : enrolmentHasNoActiveEnrolment);
      setCanDowngradeSubscription(subscriptionIsPassedPUD ? false : enrolmentHasNoActiveEnrolment);
    };

    cleanUpLocalStorageIds();
    bundledSubscriptionSpecialCases();

    // TBD-8542: "Active" while showing resubscription UI (all subs cancelled + ClueyPlus Cancel ESSL)
    const allCancelledWithClueyPlusEssl =
      subs.length > 0 &&
      subs.every((sub) => sub.subscriptionStatus === 'Cancelled') &&
      ESSLRecordHelper.hasRecordAction(esslRecords, ESSL_RECORDS_ACTIONS.CLUEY_PLUS_CANCEL);
    if (allCancelledWithClueyPlusEssl) {
      const trialSubscription = first(subs.filter((sub) => sub.subscriptionType === SUBSCRIPTION_TYPE.TRIAL));
      setShowStatusAsActive(
        !!(trialSubscription && moment(trialSubscription.subscriptionPaidUntilDate).isSameOrAfter(moment(), 'day'))
      );
    } else {
      setShowStatusAsActive(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeEnrolments,
    isLoadingActiveEnrolments,
    isLoadingSubscriptions,
    selectedStudent,
    subscriptionsData,
    esslRecords,
  ]);

  // Clean up cancel requested enrolments from local storage when active or past enrolments are loaded and found in the local storage
  useEffect(() => {
    if (pastEnrolments && !isLoadingPastEnrolments && activeEnrolments && !isLoadingActiveEnrolments) {
      const cancelRequestedEnrolments = SubscriptionLocalStorage.get(
        SUBSCRIPTION_LOCAL_STORAGE_KEYS.CLUEY_PLUS_CANCEL_ENROLMENT_REQUESTED
      );
      if (cancelRequestedEnrolments) {
        const pastEnrolmentTobeCancelled = pastEnrolments.find((enrolment) =>
          cancelRequestedEnrolments.includes(enrolment.enrolmentId)
        );
        const activeEnrolmentTobeCancelled = activeEnrolments.find(
          (enrolment) =>
            cancelRequestedEnrolments.includes(enrolment.enrolmentId) &&
            enrolment.enrolmentStatus === ENROLMENT_STATUS.CANCEL_REQUESTED
        );

        if (pastEnrolmentTobeCancelled || activeEnrolmentTobeCancelled) {
          SubscriptionLocalStorage.remove(
            SUBSCRIPTION_LOCAL_STORAGE_KEYS.CLUEY_PLUS_CANCEL_ENROLMENT_REQUESTED,
            pastEnrolmentTobeCancelled?.enrolmentId || activeEnrolmentTobeCancelled?.enrolmentId
          );
        }
      }
    }
  }, [pastEnrolments, isLoadingPastEnrolments, activeEnrolments, isLoadingActiveEnrolments]);

  // Returns the active subscription of the given type or the first active subscription if no type is provided
  // Prioritises trial subscriptions, will be used if the subscription type is Trial, needs to check if there's C+ active subscription as well
  const getSubscription = useCallback(
    (type?: SUBSCRIPTION_TYPE) => {
      let subcriptionToReturn = null;

      if (subscriptions.length) {
        if (type) {
          subcriptionToReturn = first(
            subscriptions.filter((sub) => sub.subscriptionType === type && sub.subscriptionStatus === 'Active')
          );
        } else {
          const subscriptionWithLatestPUD = first(
            subscriptions.sort((a, b) => {
              return new Date(b.subscriptionPaidUntilDate).getTime() - new Date(a.subscriptionPaidUntilDate).getTime();
            })
          );

          const hasActiveTrialSub = subscriptions.some(
            (sub) =>
              sub.subscriptionType === SUBSCRIPTION_TYPE.TRIAL &&
              sub.subscriptionStatus === 'Active' &&
              moment(sub.subscriptionPaidUntilDate).isSameOrAfter(moment(), 'day')
          );

          const hasActiveExpiredSub = subscriptions.some(
            (sub) =>
              sub.subscriptionStatus === 'Active' && moment(sub.subscriptionPaidUntilDate).isBefore(moment(), 'day')
          );

          const allCancelledWithClueyPlusEssl =
            subscriptions.every((sub) => sub.subscriptionStatus === 'Cancelled') &&
            ESSLRecordHelper.hasRecordAction(esslRecords, ESSL_RECORDS_ACTIONS.CLUEY_PLUS_CANCEL);

          if (allCancelledWithClueyPlusEssl) {
            // TBD-8542: Add logic to handle all cancelled subscriptions and ClueyPlus Cancel ESSL record
            // Check if there's a trial subscription, return the first active trial subscription if there's one
            // Else return latest PUD subscription
            const trialSubscription = first(
              subscriptions.filter((sub) => sub.subscriptionType === SUBSCRIPTION_TYPE.TRIAL)
            );

            if (
              trialSubscription &&
              moment(trialSubscription.subscriptionPaidUntilDate).isSameOrAfter(moment(), 'day')
            ) {
              subcriptionToReturn = trialSubscription;
            } else {
              subcriptionToReturn = subscriptionWithLatestPUD;
            }
          } else if (hasActiveTrialSub && hasMultipleActiveSubscriptions) {
            // Check if there's active trial subscription and multiple active subscriptions, return the first active trial subscription
            subcriptionToReturn = first(
              subscriptions.filter((sub) => sub.subscriptionType === SUBSCRIPTION_TYPE.TRIAL)
            );
          } else if (hasActiveExpiredSub) {
            // TBD-8551: Add logic to handle active expired subscription, return the subscription with the latest PUD
            subcriptionToReturn = subscriptionWithLatestPUD;
          } else {
            // Check if there's active subscription, return the first active subscription
            // If there's no active subscription, return the first subscription
            subcriptionToReturn =
              first(subscriptions.filter((subscription) => subscription.subscriptionStatus === 'Active')) ||
              first(subscriptions);
          }
        }
      }

      // Check local storage for CLUEY_PLUS_CANCEL_SUBSCRIPTION_REQUESTED if there's subscription being cancelled
      // Set subscription status to Cancelled if it exists in the local storage
      const subscriptionBeingCancelled = SubscriptionLocalStorage.get(
        SUBSCRIPTION_LOCAL_STORAGE_KEYS.CLUEY_PLUS_CANCEL_SUBSCRIPTION_REQUESTED
      );
      if (subcriptionToReturn && subscriptionBeingCancelled.includes(subcriptionToReturn.subscriptionId)) {
        subcriptionToReturn = { ...subcriptionToReturn, subscriptionStatus: 'Cancelled' };
      }

      // Check if the current subscription is a cancelled Bundled subscription and can downgrade the subscription, update status to Active
      if (
        canDowngradeSubscription &&
        subcriptionToReturn &&
        subcriptionToReturn.subscriptionType === SUBSCRIPTION_TYPE.BUNDLED
      ) {
        subcriptionToReturn = { ...subcriptionToReturn, subscriptionStatus: 'Active' };
      }

      return subcriptionToReturn || null;
    },
    [canDowngradeSubscription, hasMultipleActiveSubscriptions, subscriptions, esslRecords]
  );

  return (
    <SubscriptionContext.Provider
      value={{
        banner: {
          show: bannerShow,
          setShow: setBannerShow,
          message: bannerMessage,
          setMessage: setBannerMessage,
        },
        billing: {
          info: {
            billingInformation,
            isLoading: isLoadingBillingInformation,
            error: getBillingInformationError ? formatError(getBillingInformationError) : null,
          },
        },
        clueyPlus: {
          isLoading: isLoadingClueyPlus,
          hasMultipleActiveSubscriptions,
          subscriptions,
          esslRecords,
          isESSLRecordsLoading: isLoadingESSLRecords,
          // Cancellation confirmation dialogs
          cancellation: {
            show: cancellationShow,
            setShow: setCancellationShow,
            cancellationType,
          },
          // Student enrolment details dialog
          details: {
            show: detailsShow,
            setShow: setDetailsShow,
          },
          // Special offer dialog
          specialOffer: {
            show: showSpecialOfferMessage,
            setShow: setShowSpecialOfferMessage,
          },
          // Toast message for success/error messages
          toast: {
            message: toastMessage,
            setMessage: setToastMessage,
            show: toastShow,
            setShow: setToastShow,
          },
          // Confirmation dialog
          confirmation: {
            show: confirmationShow,
            setShow: setConfirmationShow,
            confirmationType,
          },
          canDowngradeSubscription,
          showStatusAsActive,
          getSubscription,
          refetchESSLRecords,
          showDetailsDialog: () => {
            setDetailsShow(true);
            setCancellationShow(false);
            setShowSpecialOfferMessage(false);
            setConfirmationShow(false);

            setConfirmationType(null);
            setCancellationType(null);
          },
          showSpecialOfferDialog: () => {
            setShowSpecialOfferMessage(true);
            setDetailsShow(false);
            setCancellationShow(false);
            setConfirmationShow(false);
          },
          showCancellationDialog: (cancellationType: CANCELLATION_TYPE) => {
            setCancellationType(cancellationType);
            setCancellationShow(true);
            setDetailsShow(false);
            setShowSpecialOfferMessage(false);
            setConfirmationShow(false);
          },
          showConfirmationDialog: (confirmationType: CONFIRMATION_TYPE) => {
            setConfirmationType(confirmationType);
            setConfirmationShow(true);
            setDetailsShow(false);
            setCancellationShow(false);
            setShowSpecialOfferMessage(false);
          },
        },
        student: {
          students,
          studentIsLoading: isLoadingStudents,
          studentError: studentError ? formatError(studentError) : undefined,
          selectedStudentEnrolments,
          selectedStudent,
          setSelectedStudent,
        },
        enrolments: {
          active: {
            enrolments: activeEnrolments ?? [],
            isLoading: isLoadingActiveEnrolments,
            error: activeEnrolmentsError ? formatError(activeEnrolmentsError) : null,
          },
        },
        sessions: {
          upcoming: {
            sessions: upcomingSessions ?? [],
            isLoading: isLoadingUpcomingSessions,
            error: upcomingSessionsError ? formatError(upcomingSessionsError) : null,
          },
          completed: {
            sessions: completedSessions ?? [],
            isLoading: isLoadingCompletedSessions,
            error: completedSessionsError ? formatError(completedSessionsError) : null,
          },
        },
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export const withSubscriptionContextProvider = (Component: React.ComponentType<any>) => {
  const WrappedComponent = (props: any) => (
    <SubscriptionContextProvider>
      <Component {...props} />
    </SubscriptionContextProvider>
  );
  return WrappedComponent;
};

export default function useSubscriptionContext() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscriptionContext must be used within a SubscriptionContextProvider');
  }
  return context;
}
